import { useEffect, useRef, useState } from "react";

/** Subtle fade/translate on viewport entry. Respects prefers-reduced-motion via CSS. */
export function Reveal({ children, className = "", delay = 0, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); io.disconnect(); } }, { threshold: 0.12, rootMargin: "0px 0px -40px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag ref={ref} className={`rd-reveal min-w-0 ${visible ? "rd-reveal-visible" : ""} ${className}`} style={delay ? { transitionDelay: `${delay}ms` } : undefined} {...rest}>
      {children}
    </Tag>
  );
}

export function SectionHeader({ eyebrow, title, subtitle, action, dark = false, align = "left", className = "", titleAs: TitleTag = "h2" }) {
  const center = align === "center";
  return (
    <div className={`flex flex-col gap-6 md:flex-row md:items-end md:justify-between ${center ? "md:flex-col md:items-center text-center" : ""} ${className}`}>
      <div className={center ? "max-w-2xl mx-auto" : "max-w-2xl"}>
        {eyebrow && <p className={`rd-eyebrow mb-3 ${dark ? "text-rd-action" : "text-rd-green"}`}>{eyebrow}</p>}
        <TitleTag className={`rd-h2 text-3xl sm:text-4xl lg:text-[42px] ${dark ? "text-white" : "text-rd-text"}`}>{title}</TitleTag>
        {subtitle && <p className={`mt-4 text-base sm:text-lg leading-relaxed ${dark ? "text-white/70" : "text-rd-text-2"}`}>{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

export function MetricRow({ label, value, tone = "neutral", dark = false, emphasis = false, className = "", testId }) {
  const toneClass =
    tone === "positive" ? "text-rd-green" :
    tone === "negative" ? "text-rd-negative" :
    dark ? "text-white" : "text-rd-text";
  return (
    <div className={`flex items-center justify-between gap-4 py-2.5 border-b last:border-b-0 ${dark ? "border-white/10" : "border-rd-border"} ${className}`} data-testid={testId}>
      <span className={`text-sm ${dark ? "text-white/60" : "text-rd-text-2"}`}>{label}</span>
      <span className={`font-mono font-bold tabular-nums ${emphasis ? "text-lg sm:text-xl" : "text-sm sm:text-base"} ${toneClass}`}>{value}</span>
    </div>
  );
}

export function MetricCard({ label, value, tone = "neutral", hint, className = "" }) {
  const toneClass = tone === "positive" ? "text-rd-green" : tone === "negative" ? "text-rd-negative" : "text-rd-text";
  return (
    <div className={`rd-card p-4 ${className}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-rd-text-2">{label}</p>
      <p className={`mt-1 font-mono font-bold text-xl tabular-nums ${toneClass}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-rd-muted">{hint}</p>}
    </div>
  );
}

export function FeatureCard({ icon: Icon, title, description, badge, className = "" }) {
  return (
    <div className={`rd-card rd-card-hover p-6 flex flex-col gap-4 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="w-11 h-11 rounded-xl bg-rd-navy text-white flex items-center justify-center flex-shrink-0">
          {Icon && <Icon className="w-5 h-5" aria-hidden="true" />}
        </div>
        {badge && <span className="text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-md bg-rd-green-soft text-rd-green">{badge}</span>}
      </div>
      <div>
        <h3 className="rd-h3 text-lg sm:text-xl text-rd-text">{title}</h3>
        <p className="mt-2 text-sm sm:text-base text-rd-text-2 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return <button type="button" className={`rd-btn rd-btn-primary ${className}`} {...props}>{children}</button>;
}

export function SecondaryButton({ children, onDark = false, className = "", ...props }) {
  return <button type="button" className={`rd-btn ${onDark ? "rd-btn-secondary-light" : "rd-btn-secondary-dark"} ${className}`} {...props}>{children}</button>;
}
